// =============================================
// THREE.JS SCENE SETUP
// Studio lighting, DOF, camera presets,
// system isolation, layer controls
// =============================================
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { BokehPass } from 'three/addons/postprocessing/BokehPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

let scene, camera, renderer, controls, raycaster, mouse;
let composer, bokehPass, renderPass;
let onPartClick = null;
let onPartHover = null;
let hoveredObject = null;
let dofEnabled = false;
let focusTarget = null;

// ===== INIT =====

export function initScene(canvas) {
    // Scene
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0C1221);
    scene.fog = new THREE.FogExp2(0x0C1221, 0.015); // Reduced density for clearer view

    // Camera
    camera = new THREE.PerspectiveCamera(
        45, canvas.clientWidth / canvas.clientHeight, 0.01, 100
    );
    camera.position.set(4, 2.5, 5);
    camera.lookAt(0, 1.2, 0);

    // Renderer
    renderer = new THREE.WebGLRenderer({
        canvas, antialias: true, alpha: false,
        powerPreference: 'high-performance',
    });
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    // Controls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.minDistance = 1.2;
    controls.maxDistance = 9.0;
    controls.target.set(0, 1.6, 0);
    controls.update();

    // Lighting
    setupLighting();

    // Environment map (gradient-based)
    setupEnvironment();

    // Ground
    const grid = new THREE.GridHelper(24, 48, 0x1A2540, 0x111B2E);
    grid.position.y = -2.5;
    scene.add(grid);

    // Subtle ground plane for shadow catching
    const groundGeo = new THREE.PlaneGeometry(30, 30);
    const groundMat = new THREE.ShadowMaterial({ opacity: 0.25 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -2.5;
    ground.receiveShadow = true;
    scene.add(ground);

    // Raycaster
    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();

    // Postprocessing — DOF
    setupPostprocessing(canvas);

    // Events
    canvas.addEventListener('click', onCanvasClick);
    canvas.addEventListener('mousemove', onCanvasMove);
    
    // Smooth resize handler using ResizeObserver
    const resizeObserver = new ResizeObserver(() => onResize(canvas));
    resizeObserver.observe(canvas.parentElement || canvas);

    return { scene, camera, renderer, controls };
}

// ===== LIGHTING =====

function setupLighting() {
    // Soft ambient
    const ambient = new THREE.AmbientLight(0x5A6B82, 0.45);
    scene.add(ambient);

    // Hemisphere (sky/ground)
    const hemi = new THREE.HemisphereLight(0x8EC8F7, 0x3A2A1F, 0.35);
    scene.add(hemi);

    // Key light — warm directional
    const key = new THREE.DirectionalLight(0xFFF0D6, 1.1);
    key.position.set(6, 10, 5);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 0.5;
    key.shadow.camera.far = 30;
    key.shadow.camera.left = -8;
    key.shadow.camera.right = 8;
    key.shadow.camera.top = 8;
    key.shadow.camera.bottom = -8;
    key.shadow.bias = -0.0003;
    key.shadow.normalBias = 0.02;
    scene.add(key);

    // Fill light — cooler, from left
    const fill = new THREE.DirectionalLight(0xB0C8E0, 0.4);
    fill.position.set(-4, 5, -3);
    scene.add(fill);

    // Rim / back light — slight blue for edge definition
    const rim = new THREE.DirectionalLight(0x4FA8E0, 0.3);
    rim.position.set(-3, 4, -6);
    scene.add(rim);

    // Top spot for float bowl area highlight
    const spot = new THREE.SpotLight(0xFFF3E0, 0.4, 18, Math.PI / 5, 0.6, 1);
    spot.position.set(0, 8, 0);
    spot.target.position.set(0, 0, 0);
    spot.castShadow = false;
    scene.add(spot);
    scene.add(spot.target);

    // Subtle bottom fill to see bowl details
    const bottomFill = new THREE.PointLight(0x2A4060, 0.2, 10);
    bottomFill.position.set(0, -2, 0);
    scene.add(bottomFill);
}

// ===== ENVIRONMENT =====

function setupEnvironment() {
    // Create a simple gradient environment map for realistic reflections
    const pmremGen = new THREE.PMREMGenerator(renderer);
    pmremGen.compileCubemapShader();

    const envScene = new THREE.Scene();
    const envGeo = new THREE.SphereGeometry(10, 32, 32);
    const envMat = new THREE.MeshBasicMaterial({
        side: THREE.BackSide,
        color: 0x1A2540,
    });
    envScene.add(new THREE.Mesh(envGeo, envMat));

    // Add some soft lights to the env scene for reflections
    envScene.add(new THREE.AmbientLight(0x2A3A5A, 0.5));
    const envLight = new THREE.DirectionalLight(0xFFE8CC, 0.8);
    envLight.position.set(5, 8, 3);
    envScene.add(envLight);

    const envMap = pmremGen.fromScene(envScene, 0, 0.1, 100).texture;
    scene.environment = envMap;
    pmremGen.dispose();
}

// ===== POSTPROCESSING =====

function setupPostprocessing(canvas) {
    composer = new EffectComposer(renderer);

    renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    bokehPass = new BokehPass(scene, camera, {
        focus: 5.0,
        aperture: 0.0001,
        maxblur: 0.005,
    });
    bokehPass.enabled = false;
    composer.addPass(bokehPass);

    const outputPass = new OutputPass();
    composer.addPass(outputPass);
}

// ===== INTERACTION =====

function onCanvasClick(e) {
    const rect = e.target.getBoundingClientRect();
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(scene.children, true);

    for (const hit of intersects) {
        const partId = getPartId(hit.object);
        if (partId && onPartClick) {
            onPartClick(partId, hit.object, hit.point);
            return;
        }
    }

    // Clicked empty space — clear DOF
    if (onPartClick) onPartClick(null, null, null);
}

let lastMouseMoveTime = 0;
function onCanvasMove(e) {
    const now = performance.now();
    if (now - lastMouseMoveTime < 32) return; // Throttle to ~30fps
    lastMouseMoveTime = now;

    const rect = e.target.getBoundingClientRect();
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(scene.children, true);

    let found = null;
    for (const hit of intersects) {
        const partId = getPartId(hit.object);
        if (partId) {
            found = hit.object;
            break;
        }
    }

    if (found !== hoveredObject) {
        if (hoveredObject && hoveredObject.material && hoveredObject.userData._origEmissive !== undefined) {
            hoveredObject.material.emissiveIntensity = hoveredObject.userData._origEmissive;
        }
        hoveredObject = found;
        if (hoveredObject && hoveredObject.material) {
            hoveredObject.userData._origEmissive = hoveredObject.material.emissiveIntensity || 0;
            hoveredObject.material.emissiveIntensity = Math.min(hoveredObject.userData._origEmissive + 0.35, 0.8);
        }
        e.target.style.cursor = found ? 'pointer' : 'default';
        if (onPartHover) onPartHover(found ? getPartId(found) : null);
    }
}

function getPartId(obj) {
    let current = obj;
    while (current) {
        if (current.userData && current.userData.partId) return current.userData.partId;
        current = current.parent;
    }
    return null;
}

function onResize(canvas) {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    if (composer) composer.setSize(w, h);
}

// ===== PUBLIC API =====

export function setPartClickHandler(fn) { onPartClick = fn; }
export function setPartHoverHandler(fn) { onPartHover = fn; }

export function animate(callback) {
    function loop(time) {
        requestAnimationFrame(loop);
        
        // --- CINEMATIC AUTO-SHOWCASE LOGIC ---
        if (controls && controls.autoRotate) {
            // 1. Cinematic Glide: Drift to showcase distance AND center POV
            const showcaseDist = 6.8;
            const currentDist = camera.position.distanceTo(controls.target);
            
            // Re-center focus if panned away
            const centerTarget = new THREE.Vector3(0, 1.6, 0);
            controls.target.lerp(centerTarget, 0.02);

            if (currentDist < showcaseDist - 0.2) {
                const dir = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
                const targetPos = controls.target.clone().add(dir.multiplyScalar(showcaseDist));
                camera.position.lerp(targetPos, 0.025);
            }

            // 2. Satisfying Transition: Tilt camera to a cinematic angle (around 75°)
            // if looking from top/bottom poles. 1.3 is approx 75 degrees.
            const targetPolar = 1.3;
            const currentPolar = controls.getPolarAngle();
            if (Math.abs(currentPolar - targetPolar) > 0.05) {
                // We use setAzimuthalAngle style logic or just direct camera spherical move
                // Simple lerp for polar angle is best handled by controls internally if we can,
                // but OrbitControls polar range is restrictive.
                // We'll just let the natural orbit handle it if we are panned correctly.
            }

            // 3. Cinematic Altitude "Breathing": Bob up and down slightly
            // CRITICAL: Only bob if camera has settled to prevent "bouncing" against user input
            if (currentDist > showcaseDist - 0.5) {
                const bobAmount = 0.4;
                const bobFreq = 0.001;
                const targetY = 1.6 + Math.sin(time * bobFreq) * bobAmount;
                controls.target.y = THREE.MathUtils.lerp(controls.target.y, targetY, 0.02);
            }

            controls.update();

            // 3. Update HUD Telemetry (if UI is present)
            const hud = document.getElementById('cinematic-hud');
            if (hud && !hud.classList.contains('hidden')) {
                // Azimuth in clean 0-360 range
                let rawAz = (controls.getAzimuthalAngle() * 180 / Math.PI);
                while (rawAz < 0) rawAz += 360;
                const azimuth = (rawAz % 360).toFixed(1);
                
                const altitude = camera.position.y.toFixed(2);
                const azEl = document.getElementById('hud-azimuth');
                const alEl = document.getElementById('hud-altitude');
                if (azEl) azEl.textContent = azimuth;
                if (alEl) alEl.textContent = altitude;
                
                // Technical system analysis cycling
                const systems = ["IDLE SYSTEM", "VENTURI SUCTION", "MAIN METERING", "MIXTURE CONTROL", "ACCELERATOR PUMP", "FLOAT CHAMBER"];
                const systemIdx = Math.floor((time / 2000) % systems.length);
                const subEl = document.getElementById('hud-subsystem');
                if (subEl) subEl.textContent = `ANALYZING: ${systems[systemIdx]}`;

                // Update technical clock
                const clock = hud.querySelector('.hud-clock');
                if (clock) clock.textContent = new Date().toLocaleTimeString();
            }
        } else if (controls) {
            controls.update();
        }

        if (callback) callback(time);

        // Update DOF focus if tracking a target
        if (dofEnabled && focusTarget && bokehPass.enabled) {
            const dist = camera.position.distanceTo(focusTarget);
            bokehPass.uniforms['focus'].value = dist;
        }

        // Pulse effect for highlighted parts
        const pulse = 0.5 + Math.sin(time * 0.005) * 0.3;
        scene.traverse(child => {
            if (child.userData?._highlighted && child.material) {
                child.material.emissiveIntensity = pulse;
            }
        });

        if (composer && bokehPass.enabled) {
            composer.render();
        } else {
            renderer.render(scene, camera);
        }
    }
    requestAnimationFrame(loop);
}

export function getScene() { return scene; }
export function getCamera() { return camera; }
export function getRenderer() { return renderer; }

// ===== HIGHLIGHTING =====

export function highlightPart(partId) {
    scene.traverse(child => {
        if (child.userData && child.userData.partId === partId && child.material) {
            if (child.userData._origEmissive === undefined) {
                child.userData._origEmissive = child.material.emissiveIntensity || 0;
            }
            child.material.emissiveIntensity = 0.5;
            child.userData._highlighted = true;
        }
    });
}

export function clearHighlights() {
    scene.traverse(child => {
        if (child.userData && child.userData._highlighted && child.material) {
            child.material.emissiveIntensity = child.userData._origEmissive || 0;
            child.userData._highlighted = false;
        }
    });

    // Also clear X-ray if any
    setXRayMode(null);
}

const _xrayMaterials = new Map();

/**
 * X-Ray mode: Highlight a part AND make obstructing geometry transparent
 */
export function setXRayMode(targetPartId) {
    // 1. Clear previous X-ray state
    _xrayMaterials.forEach((origMat, obj) => {
        obj.material = origMat;
    });
    _xrayMaterials.clear();

    if (!targetPartId) return;

    // 2. Find the target mesh(es)
    const targets = [];
    scene.traverse(child => {
        if (child.userData?.partId === targetPartId && child.isMesh) {
            targets.push(child);
        }
    });

    if (targets.length === 0) return;

    // 3. Highlight targets
    targets.forEach(t => {
        if (!t.userData._origEmissive) t.userData._origEmissive = t.material.emissiveIntensity || 0;
        t.material.emissiveIntensity = 0.8;
        t.userData._highlighted = true;
    });

    // 4. Find anything between camera and targets
    const raycaster = new THREE.Raycaster();
    targets.forEach(target => {
        const worldPos = new THREE.Vector3();
        target.getWorldPosition(worldPos);
        
        const dir = worldPos.clone().sub(camera.position).normalize();
        raycaster.set(camera.position, dir);
        raycaster.far = camera.position.distanceTo(worldPos);

        const intersects = raycaster.intersectObjects(scene.children, true);
        intersects.forEach(hit => {
            const obj = hit.object;
            if (obj !== target && obj.isMesh && obj.userData?.partId !== targetPartId) {
                if (!_xrayMaterials.has(obj)) {
                    _xrayMaterials.set(obj, obj.material);
                    const xrayMat = obj.material.clone();
                    xrayMat.transparent = true;
                    xrayMat.opacity = 0.15;
                    xrayMat.depthWrite = false;
                    obj.material = xrayMat;
                }
            }
        });
    });
}

// ===== DEPTH OF FIELD =====

export function setDOFFocus(targetPosition) {
    if (!bokehPass) return;
    if (targetPosition) {
        focusTarget = targetPosition.clone();
        bokehPass.uniforms['aperture'].value = 0.0008;
        bokehPass.uniforms['maxblur'].value = 0.006;
        bokehPass.enabled = true;
        dofEnabled = true;
    } else {
        bokehPass.enabled = false;
        dofEnabled = false;
        focusTarget = null;
    }
}

// ===== VIEW MODES =====

export function setViewMode(mode) {
    scene.traverse(child => {
        if (!child.userData || !child.userData.partId) return;
        if (!child.material) return;

        // Restore original material if stored
        if (child.userData._origMaterial) {
            child.material = child.userData._origMaterial;
        }

        switch (mode) {
            case 'external':
                child.visible = !child.userData.internal;
                if (child.material.transparent !== undefined) {
                    child.material.transparent = child.userData._wasTransparent || false;
                    child.material.opacity = child.userData._origOpacity || 1;
                }
                break;
            case 'cutaway':
                child.visible = true;
                if (child.userData.housing) {
                    if (!renderer.localClippingEnabled) renderer.localClippingEnabled = true;
                    child.material = child.material.clone();
                    child.material.clippingPlanes = [
                        new THREE.Plane(new THREE.Vector3(0, 0, 1), 0.1)
                    ];
                    child.material.clipShadows = true;
                }
                break;
            case 'transparent':
                child.visible = true;
                if (child.userData.housing) {
                    // Store original
                    if (!child.userData._origMaterial) {
                        child.userData._origMaterial = child.material;
                        child.userData._wasTransparent = child.material.transparent;
                        child.userData._origOpacity = child.material.opacity;
                    }
                    child.material = child.material.clone();
                    child.material.transparent = true;
                    child.material.opacity = 0.12;
                    child.material.depthWrite = false;
                }
                break;
        }
    });
}

// ===== LAYER SYSTEM =====

export function setLayerLevel(level) {
    // level 1: external only
    // level 2: external + major cavities
    // level 3: everything including small passages
    scene.traverse(child => {
        if (!child.userData || !child.userData.partId) return;
        const meshLayer = child.userData.layer || 1;
        child.visible = meshLayer <= level;
    });
}

// ===== SYSTEM ISOLATION =====

let isolatedSystem = null;
const _storedMaterials = new Map();

export function setSystemIsolation(system) {
    // Restore all first
    _storedMaterials.forEach((origMat, child) => {
        child.material = origMat;
    });
    _storedMaterials.clear();
    isolatedSystem = system;

    if (!system) return; // show all

    scene.traverse(child => {
        if (!child.userData || !child.userData.partId) return;
        if (!child.material || !child.isMesh) return;

        if (child.userData.system !== system) {
            _storedMaterials.set(child, child.material);
            const ghost = new THREE.MeshStandardMaterial({
                color: 0x333344,
                metalness: 0.05,
                roughness: 0.9,
                transparent: true,
                opacity: 0.06,
                depthWrite: false,
            });
            child.material = ghost;
        } else {
            // Boost the selected system
            if (child.material.emissiveIntensity !== undefined) {
                _storedMaterials.set(child, child.material);
                const boosted = child.material.clone();
                boosted.emissiveIntensity = Math.min((boosted.emissiveIntensity || 0) + 0.2, 0.6);
                child.material = boosted;
            }
        }
    });
}

// ===== CAMERA PRESETS =====

export function setViewPreset(preset) {
    switch (preset) {
        case '3d':
            camera.position.set(4, 2.5, 5);
            controls.target.set(0, 1.0, 0);
            camera.fov = 45;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'textbook':
            // Side-view matching textbook cross-section diagrams
            // Looking from the right side, slightly elevated
            camera.position.set(5.5, 1.5, 0.3);
            controls.target.set(0, 1.0, 0);
            camera.fov = 30; // narrow FOV for more orthographic feel
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'front':
            camera.position.set(0, 1.5, 7);
            controls.target.set(0, 1.0, 0);
            camera.fov = 40;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'top':
            camera.position.set(0, 10, 0.1);
            controls.target.set(0, 1.0, 0);
            camera.fov = 40;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'process_cruise':
            camera.position.set(4.5, 1.5, 3.5);
            controls.target.set(0, 1.5, 0);
            camera.fov = 35;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'process_idle':
            camera.position.set(2.5, 2.5, 2.5);
            controls.target.set(0, 2.5, 0);
            camera.fov = 25;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'process_mixture':
            camera.position.set(-4, 0, 3);
            controls.target.set(-1, -0.2, 0);
            camera.fov = 25;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'process_accel':
            camera.position.set(3, -0.5, -4);
            controls.target.set(1, -0.5, -0.5);
            camera.fov = 22;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'process_float':
            camera.position.set(4, -1, 3);
            controls.target.set(0, -0.8, 0);
            camera.fov = 28;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'process_jets':
            camera.position.set(0, -2, 5);
            controls.target.set(0, -0.5, 0);
            camera.fov = 22;
            camera.updateProjectionMatrix();
            controls.update();
            break;

        case 'process_air_bleed':
            camera.position.set(0, 4, 4);
            controls.target.set(0, 0.5, 0);
            camera.fov = 25;
            camera.updateProjectionMatrix();
            controls.update();
            break;
    }
}

export function setHousingOpacity(opacity) {
    scene.traverse(child => {
        if (child.isMesh) {
            const isHousing = child.userData.partId && (child.userData.partId.includes('housing') || child.userData.partId.includes('body'));
            
            if (isHousing) {
                child.material = child.material.clone();
                child.material.transparent = opacity < 1;
                child.material.opacity = opacity;
                child.material.depthWrite = (opacity > 0.5);
            }
        }
    });
}

export function snapCameraToPart(cameraData) {
    if (!cameraData) return;
    const { pos, target } = cameraData;
    camera.position.set(pos.x, pos.y, pos.z);
    controls.target.set(target.x, target.y, target.z);
    controls.update();
}

/**
 * Visual simulations for troubleshooting symptoms
 */
export function simulateSymptom(action) {
    // Clear previous simulations
    clearHighlights();
    
    switch (action) {
        case 'simulate_float_failure':
            // Sinking float
            setXRayMode('float-bowl');
            break;
        case 'simulate_accel_boot_fail':
            setXRayMode('accel-pump');
            break;
        case 'simulate_lean_idle':
            setXRayMode('idle-mixture-screw');
            break;
        case 'simulate_mixture_leak':
            setXRayMode('mixture-valve');
            break;
        case 'simulate_accel_fail':
            setXRayMode('accel-pump');
            break;
    }
}
/**
 * Rotates the 3D throttle valve components based on input percentage (0-100)
 */
export function setThrottlePosition(percent) {
    // Rotation maps from ~20 degrees (idle) to ~90 degrees (WOT)
    // Shaft is along X axis, so we rotate around X
    const angle = (percent / 100) * 1.3; 
    
    scene.traverse(child => {
        if (child.userData.partId === 'throttle-valve' || child.userData.partId === 'throttle-shaft') {
            child.rotation.x = angle;
        }
    });
}

/**
 * Experimental: Adds a holographic glow to the model when physics lab is active
 */
export function setLabAesthetic(active) {
    scene.traverse(child => {
        if (child.isMesh && !child.userData.housing) {
            if (active) {
                child.userData._prevEmissive = child.material.emissive?.clone();
                child.material.emissive?.set(0x00f2fe);
                child.material.emissiveIntensity = 0.2;
            } else if (child.userData._prevEmissive) {
                child.material.emissive?.copy(child.userData._prevEmissive);
                child.material.emissiveIntensity = 0.1;
            }
        }
    });
}

/**
 * Adjusts camera vertical target via HUD slider (0-100)
 */
export function setCameraElevation(percent) {
    // Calibrated: 50% = 1.6 (Absolute Center)
    // Range: -0.4 to 3.6 (Total 4.0)
    const yTarget = -0.4 + (percent / 100) * 4.0;
    const diff = yTarget - controls.target.y;
    
    // Disable damping temporarily so OrbitControls doesn't "fight" the manual shift and bounce
    const wasDamping = controls.enableDamping;
    controls.enableDamping = false;
    controls.target.y = yTarget;
    camera.position.y += diff;
    controls.update();
    controls.enableDamping = wasDamping;
}

/**
 * Controls whether the camera rotates automatically around the target
 */
export function setAutoRotate(enabled) {
    if (!controls) return;
    controls.autoRotate = enabled;
    // High-speed cinematic "discovery" spin
    controls.autoRotateSpeed = 6.0; 
}
